import React, { useState, useEffect, useRef } from 'react';
import { FiCheck, FiX, FiCreditCard, FiDownload, FiCalendar, FiDollarSign, FiArrowUp, FiArrowDown, FiAlertTriangle, FiMail, FiExternalLink, FiHardDrive, FiPlus, FiMinus, FiArchive } from 'react-icons/fi';

const TIER_DETAILS = {
  // New premium tier structure (2026)
  free: {
    name: 'Personal',
    price: 7,
    storage: '100 GB',
    storageBytes: 100 * 1024 * 1024 * 1024,
    albums: 'Up to 25 albums',
    includedSeats: 1,
    extraSeatsEnabled: false,
    maxAddonTB: 2,
    clientAlbums: false,
    clientSelection: false,
    supportLevel: 'email',
    supportSla: '5-7 business days',
    features: [
      '100 GB private photo storage',
      'Up to 25 albums',
      'Secure shareable links',
      'RAW file support',
      'Works on desktop and mobile',
      'Email support (5-7 business days)'
    ],
    limitations: [
      'No team collaboration',
      'No client galleries',
      'Max 2TB storage add-ons'
    ]
  },
  pro: {
    name: 'Pro',
    price: 39,
    storage: '500 GB',
    storageBytes: 500 * 1024 * 1024 * 1024,
    albums: 'Unlimited albums',
    includedSeats: 1,
    extraSeatsEnabled: true,
    extraUserPrice: 8,
    maxAddonTB: null,
    clientAlbums: false,
    clientAlbumsLimit: 0,
    clientSelection: false,
    supportLevel: 'email',
    supportSla: '3-5 business days',
    features: [
      '500 GB private photo storage',
      'Unlimited albums',
      'Client sharing links',
      'Faster previews',
      'Basic analytics',
      'Watermarks & batch rename',
      '+$8/seat for additional users',
      'Email support (3-5 business days)'
    ],
    limitations: [
      'No client galleries',
      'No proofing/selection'
    ]
  },
  studio: {
    name: 'Studio',
    price: 99,
    storage: '2 TB',
    storageBytes: 2 * 1024 * 1024 * 1024 * 1024,
    albums: 'Unlimited albums',
    includedSeats: 1,
    extraSeatsEnabled: true,
    extraUserPrice: 8,
    maxAddonTB: null,
    clientAlbums: true,
    clientAlbumsLimit: null,
    clientSelection: true,
    supportLevel: 'email',
    supportSla: '48-72h',
    features: [
      '2 TB private photo storage',
      'Client galleries & proofing',
      'Priority previews',
      'Basic analytics',
      'The Strip (private social feed)',
      '+$8/seat for additional users',
      'Email support (48-72h)'
    ],
    limitations: [
      'No priority compute'
    ]
  },
  business: {
    name: 'Business',
    price: 179,
    storage: '5 TB',
    storageBytes: 5 * 1024 * 1024 * 1024 * 1024,
    albums: 'Unlimited albums',
    includedSeats: 5,
    extraSeatsEnabled: true,
    extraUserPrice: 8,
    maxAddonTB: null,
    clientAlbums: true,
    clientAlbumsLimit: null,
    clientSelection: true,
    clientUploads: true,
    supportLevel: 'email',
    supportSla: '24-48h',
    features: [
      '5 TB private photo storage',
      'Team collaboration (5 seats included)',
      'Client galleries & proofing',
      'Client uploads',
      'Priority compute (Burst)',
      'Advanced analytics',
      '+$8/seat for additional users',
      'Email support (24-48h)'
    ],
    limitations: []
  },
  agency: {
    name: 'Agency',
    price: 399,
    storage: '12 TB',
    storageBytes: 12 * 1024 * 1024 * 1024 * 1024,
    albums: 'Unlimited albums',
    includedSeats: 20,
    extraSeatsEnabled: true,
    extraUserPrice: 8,
    maxAddonTB: 0,
    clientAlbums: true,
    clientAlbumsLimit: null,
    clientSelection: true,
    clientUploads: true,
    whiteLabel: true,
    apiAccess: true,
    sla: true,
    supportLevel: 'priority',
    supportSla: '<24h',
    features: [
      '12 TB private photo storage',
      'Team collaboration (20 seats included)',
      'Client galleries & proofing',
      'White-label sharing',
      'API access',
      'SLA guarantee',
      'Priority support (<24h)'
    ],
    limitations: [
      'Contact us for extra storage'
    ]
  },
  // Legacy tier mappings (for backwards compatibility display)
  'starter-500': {
    name: 'Starter (Legacy)',
    price: 12,
    storage: '500 GB',
    storageBytes: 500 * 1024 * 1024 * 1024,
    albums: 'Unlimited albums',
    features: ['Legacy plan - consider upgrading to Pro'],
    limitations: []
  },
  'studio-archive': {
    name: 'Studio Archive (Legacy)',
    price: 22,
    storage: '1 TB',
    storageBytes: 1024 * 1024 * 1024 * 1024,
    albums: 'Unlimited albums',
    features: ['Legacy plan - consider upgrading to Studio'],
    limitations: []
  },
  'client-studio': {
    name: 'Client Studio (Legacy)',
    price: 32,
    storage: '2 TB',
    storageBytes: 2 * 1024 * 1024 * 1024 * 1024,
    albums: 'Unlimited albums',
    features: ['Legacy plan - consider upgrading to Business'],
    limitations: []
  },
  'agency-team': {
    name: 'Agency Team (Legacy)',
    price: 42,
    storage: '3 TB',
    storageBytes: 3 * 1024 * 1024 * 1024 * 1024,
    albums: 'Unlimited albums',
    features: ['Legacy plan - consider upgrading to Agency'],
    limitations: []
  },
  'agency-team-8tb': {
    name: 'Scale (Legacy)',
    price: 99,
    storage: '8 TB',
    storageBytes: 8 * 1024 * 1024 * 1024 * 1024,
    albums: 'Unlimited albums',
    features: ['Legacy plan - consider upgrading to Agency'],
    limitations: []
  },
  essential: {
    name: 'Essential (Legacy)',
    price: 22,
    storage: '1 TB',
    albums: 'Unlimited albums',
    features: [],
    limitations: []
  },
  professional: {
    name: 'Professional (Legacy)',
    price: 32,
    storage: '2 TB',
    albums: 'Unlimited albums',
    features: [],
    limitations: []
  },
  starter: {
    name: 'Starter (Legacy)',
    price: 12,
    storage: '500 GB',
    albums: 'Unlimited albums',
    features: [],
    limitations: []
  },
  advanced: {
    name: 'Advanced (Legacy)',
    price: 32,
    storage: '2 TB',
    albums: 'Unlimited albums',
    features: [],
    limitations: []
  },
  early_access: {
    name: 'Early Access',
    price: 0,
    storage: '100 GB',
    storageBytes: 100 * 1024 * 1024 * 1024,
    albums: '25 albums',
    includedSeats: 1,
    extraSeatsEnabled: false,
    extraUserPrice: 0,
    maxAddonTB: 2,
    clientAlbums: false,
    clientAlbumsLimit: null,
    clientSelection: false,
    clientUploads: false,
    supportLevel: 'email',
    supportSla: '5-7 business days',
    features: [
      '100 GB private photo storage',
      'Secure sharing links',
      'RAW file support',
      'Mobile web access',
      'Email support'
    ],
    limitations: []
  }
};

export default function Billing() {
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [stripeConfig, setStripeConfig] = useState(null);
  const upgradeRef = useRef(null);

  const storageUsedBytes =
    (subscription?.storage && (subscription.storage.used ?? subscription.storage.usedBytes ?? null)) ??
    (usage?.storage && (usage.storage.used ?? usage.storage.usedBytes ?? null)) ??
    (analytics?.totalStorage ?? 0);
  const storageLimitBytes = (subscription?.storage && (subscription.storage.limit ?? null)) ?? (usage?.storage?.limit ?? null);
  const rawStoragePercent = storageLimitBytes ? (storageUsedBytes / storageLimitBytes) * 100 : 0;
  const storagePercent = Number.isFinite(rawStoragePercent) ? Math.min(100, rawStoragePercent) : 0;
  const storageDisplayPercent = storagePercent >= 10
    ? Math.round(storagePercent)
    : Math.round(storagePercent * 10) / 10;
  const storageBarPercent = storagePercent === 0 && storageUsedBytes > 0 ? 1 : storagePercent;
  const showStorageAlert = storageLimitBytes && storagePercent >= 85;
  const storageAlertTone = storagePercent >= 95 ? 'critical' : 'warning';

  useEffect(() => {
    loadSubscription();
    // Load Stripe.js for future integration
    loadStripe();
  }, []);

  async function loadStripe() {
    try {
      // Get Stripe configuration
      const res = await fetch('/api/subscription/config', { credentials: 'include' });
      if (res.ok) {
        const config = await res.json();
        setStripeConfig(config);

        if (config.configured && config.publishableKey) {
          // Load Stripe.js from CDN
          if (!window.Stripe) {
            const script = document.createElement('script');
            script.src = 'https://js.stripe.com/v3/';
            script.async = true;
            script.onload = () => {
              console.log('Stripe.js loaded');
              window.Stripe(config.publishableKey);
              setStripeLoaded(true);
            };
            document.body.appendChild(script);
          } else {
            window.Stripe(config.publishableKey);
            setStripeLoaded(true);
          }
        } else {
          console.log('Stripe not configured - using test mode');
          setStripeLoaded(true);
        }
      }
    } catch (error) {
      console.error('Failed to load Stripe config:', error);
      setStripeLoaded(true); // Continue in test mode
    }
  }

  function formatBytes(bytes) {
    if (!bytes || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  }

  function parseStorageToBytes(label) {
    if (!label) return null;
    const norm = String(label).toLowerCase();
    if (norm.includes('unlimited')) return null;
    const value = parseFloat(norm.replace(/[^0-9.]/g, ''));
    if (Number.isNaN(value)) return null;
    if (norm.includes('tb')) return value * 1024 ** 4;
    if (norm.includes('gb')) return value * 1024 ** 3;
    return null;
  }

  function getTierLimitBytes(tierId) {
    const tier = TIER_DETAILS[tierId];
    return parseStorageToBytes(tier?.storage);
  }

  function getUsedBytes() {
    return (subscription?.storage && (subscription.storage.usedBytes ?? null))
      ?? (usage?.storage && (usage.storage.usedBytes ?? null))
      ?? (analytics?.totalStorage ?? 0);
  }

  async function loadSubscription() {
    try {
      const [subRes, usageRes, analyticsRes] = await Promise.all([
        fetch('/api/subscription', { credentials: 'include' }),
        fetch('/api/subscription/usage', { credentials: 'include' }),
        fetch('/api/analytics', { credentials: 'include' })
      ]);

      if (subRes.ok) {
        const data = await subRes.json();
        setSubscription(data);
      }

      if (usageRes.ok) {
        const usageData = await usageRes.json();
        setUsage(usageData);
      }
      if (analyticsRes.ok) {
        const analyticsData = await analyticsRes.json();
        setAnalytics(analyticsData);
      }
    } catch (error) {
      console.error('Failed to load subscription:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleUpgrade(tier) {
    setUpgrading(true);
    setSelectedTier(tier);

    try {
      if (subscription?.canChangePlan) {
        await changeTier(tier, false);
        setShowUpgrade(false);
        return;
      }
      // Check if Stripe is loaded
      if (!stripeLoaded) {
        console.log('Waiting for Stripe to load...');
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      console.log('Creating checkout session for tier:', tier);

      const res = await fetch('/api/subscription/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tier })
      });

      if (!res.ok) {
        const failure = await res.json().catch(() => ({}));
        throw new Error(failure.error || 'Unable to start checkout');
      }
      if (res.ok) {
        const data = await res.json();
        if (data.checkoutUrl && data.checkoutUrl !== '#') {
          // Redirect to Stripe checkout
          console.log('Redirecting to Stripe checkout...');
          window.location.href = data.checkoutUrl;
        } else if (data.testMode) {
          // Test mode - direct tier update
          console.log('Test mode: Updating tier directly');
          alert(`Test mode: Upgrading to ${tier} plan`);
          await updateTierDirect(tier);
        }
      }
    } catch (error) {
      console.error('Upgrade failed:', error);
      alert(error.message || 'Upgrade failed. Please try again.');
    } finally {
      setUpgrading(false);
      setSelectedTier(null);
    }
  }

  async function updateTierDirect(tier) {
    try {
      const res = await fetch('/api/subscription/update-tier', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tier })
      });

      if (res.ok) {
        alert(`Successfully upgraded to ${TIER_DETAILS[tier].name}!`);
        loadSubscription();
        setShowUpgrade(false);
      }
    } catch (error) {
      console.error('Direct tier update failed:', error);
      throw error;
    }
  }

  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingError, setBillingError] = useState(null);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [targetTier, setTargetTier] = useState(null);
  const [showStorageModal, setShowStorageModal] = useState(false);
  const [storageAmount, setStorageAmount] = useState(1);
  const [addingStorage, setAddingStorage] = useState(false);
  const [storageInfo, setStorageInfo] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [canceling, setCanceling] = useState(false);

  async function handleCancelSubscription() {
    setCanceling(true);
    try {
      const res = await fetch('/api/subscription/cancel', {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setShowCancelModal(false);
        await loadSubscription();
      } else {
        alert(data.error || 'Unable to cancel. Please try again or use the billing portal.');
      }
    } catch (error) {
      console.error('Cancel failed:', error);
      alert('Unable to cancel. Please try again or contact support.');
    } finally {
      setCanceling(false);
    }
  }

  async function handleResumeSubscription() {
    setCanceling(true);
    try {
      const res = await fetch('/api/subscription/resume', {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        await loadSubscription();
      } else {
        alert(data.error || 'Unable to resume. Please try again or contact support.');
      }
    } catch (error) {
      console.error('Resume failed:', error);
      alert('Unable to resume. Please try again or contact support.');
    } finally {
      setCanceling(false);
    }
  }

  async function handleBillingPortal() {
    try {
      setBillingError(null);
      const res = await fetch('/api/subscription/portal', {
        method: 'POST',
        credentials: 'include'
      });

      const data = await res.json();

      if (res.ok && data.portalUrl && data.portalUrl !== '#') {
        window.location.href = data.portalUrl;
      } else if (data.error === 'No Stripe customer on file') {
        // User hasn't set up billing yet
        setShowBillingModal(true);
      } else if (data.testMode) {
        setShowBillingModal(true);
      } else {
        setBillingError('Unable to access billing portal. Please contact support.');
      }
    } catch (error) {
      console.error('Billing portal failed:', error);
      setBillingError('Unable to open billing portal. Please contact support.');
    }
  }

  function openChangePlanModal(tier) {
    setTargetTier(tier);
    setShowChangePlanModal(true);
  }

  async function loadStorageInfo() {
    try {
      const res = await fetch('/api/subscription/storage-addons', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStorageInfo(data);
      }
    } catch (error) {
      console.error('Failed to load storage info:', error);
    }
  }

  function openStorageModal() {
    loadStorageInfo();
    setStorageAmount(1);
    setShowStorageModal(true);
  }

  async function handleAddStorage() {
    if (storageAmount < 1 || storageAmount > 10) return;

    setAddingStorage(true);
    try {
      const res = await fetch('/api/subscription/add-storage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ amount: storageAmount })
      });

      const data = await res.json();

      if (res.ok) {
        if (data.checkoutUrl && data.checkoutUrl !== '#') {
          window.location.href = data.checkoutUrl;
        } else if (data.testMode) {
          alert(`Test mode: Added ${storageAmount} TB of storage. Total extra: ${data.totalExtra}`);
          setShowStorageModal(false);
          loadSubscription();
        }
      } else {
        alert(data.error || 'Failed to add storage');
      }
    } catch (error) {
      console.error('Add storage failed:', error);
      alert('Failed to add storage. Please try again.');
    } finally {
      setAddingStorage(false);
    }
  }

  function getTierOrder() {
    // New premium tier structure (2026)
    return ['free', 'pro', 'studio', 'business', 'agency'];
  }

  function canonicalTierFromSubscription(sub) {
    const raw = String(sub?.tier || '').toLowerCase();
    const byId = {
      // New tier IDs (2026)
      'free': 'free',
      'pro': 'pro',
      'studio': 'studio',
      'business': 'business',
      'agency': 'agency',
      // Legacy tier mappings → new equivalents
      'starter': 'pro',
      'starter-500': 'pro',
      'essential': 'studio',
      'studio-archive': 'studio',
      'professional': 'business',
      'client-studio': 'business',
      'advanced': 'business',
      'agency-team': 'agency',
      'agency-team-8tb': 'agency',
      'scale': 'agency',
      'beta_vip': 'early_access',
      'early_access': 'early_access',
    };
    if (byId[raw]) return byId[raw];
    const name = String(sub?.tierName || '').toLowerCase();
    if (name.includes('agency')) return 'agency';
    if (name.includes('business')) return 'business';
    if (name.includes('studio')) return 'studio';
    if (name.includes('pro')) return 'pro';
    if (name.includes('personal') || name.includes('free')) return 'free';
    return 'free';
  }

  function getAvailableUpgrades() {
    if (!subscription) return [];
    const currentTier = canonicalTierFromSubscription(subscription);
    if (currentTier === 'early_access') return [];
    const tierOrder = getTierOrder();
    if (!tierOrder.includes(currentTier)) return tierOrder; // fallback

    const currentIndex = tierOrder.indexOf(currentTier);
    return tierOrder.slice(currentIndex + 1);
  }

  function isDowngrade(targetTier) {
    if (!subscription) return false;
    const order = getTierOrder();
    const currentIdx = order.indexOf(canonicalTierFromSubscription(subscription));
    const targetIdx = order.indexOf(targetTier);
    // If current tier is not in list, assume any target in list is an upgrade/sidegrade unless logic dictates otherwise
    if (currentIdx === -1) return false;
    return targetIdx < currentIdx;
  }

  async function changeTier(tier, atPeriodEnd = false) {
    try {
      const res = await fetch('/api/subscription/change-tier', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tier, at_period_end: atPeriodEnd })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to change tier');
      }
      const data = await res.json();
      if (data.success) {
        if (data.scheduled) {
          const when = data.effectiveAt ? new Date(data.effectiveAt).toLocaleString() : 'your next billing date';
          alert(`Your downgrade to ${TIER_DETAILS[tier].name} is scheduled for ${when}.`);
        } else {
          alert(`Your plan has been updated to ${TIER_DETAILS[tier].name}.`);
        }
        await loadSubscription();
      }
    } catch (error) {
      console.error('Change tier failed:', error);
      alert(error.message || 'Unable to change plan. Please try again later.');
      throw error;
    }
  }

  async function handleTierAction(tier) {
    // Open confirmation modal for both upgrades and downgrades
    openChangePlanModal(tier);
  }

  async function confirmPlanChange() {
    const tier = targetTier;
    if (!tier) return;

    // Upgrades go through Stripe Checkout for proration; downgrades change in place.
    if (isDowngrade(tier)) {
      const limit = getTierLimitBytes(tier);
      const used = getUsedBytes();
      if (limit && used > limit) {
        alert(`You're using ${formatBytes(used)}. To downgrade, you need to be under ${formatBytes(limit)} or add extra storage ($29 per TB).`);
        setShowChangePlanModal(false);
        return;
      }
      setUpgrading(true);
      setSelectedTier(tier);
      try {
        // Default to scheduling at period end per product decision
        await changeTier(tier, true);
        setShowChangePlanModal(false);
      } finally {
        setUpgrading(false);
        setSelectedTier(null);
      }
      return;
    }
    // Upgrade path (existing logic)
    setShowChangePlanModal(false);
    return handleUpgrade(tier);
  }

  function getAvailableDowngrades() {
    if (!subscription) return [];
    const currentTier = canonicalTierFromSubscription(subscription);
    if (currentTier === 'early_access') return [];
    const tierOrder = getTierOrder();
    const currentIndex = tierOrder.indexOf(currentTier);
    if (currentIndex === -1) return [];
    return tierOrder.slice(0, currentIndex);
  }

  function scrollToUpgrade() {
    if (upgradeRef.current) {
      upgradeRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }

  if (loading) {
    return (
      <div className="billing-page">
        <div className="billing-header">
          <h2>Billing & Subscription</h2>
          <p>Loading subscription details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="billing-page">
      <div className="billing-header">
        <h2>Billing & Subscription</h2>
        <p>Manage your subscription and billing preferences</p>
      </div>

      {/* Current Plan Overview */}
      {subscription && (
        <div className="current-plan-section">
          <div className="current-plan-card">
              <div className="plan-info">
                <div className="plan-badge-large">
                <span className="plan-name">{subscription.tierName}</span>
                <span className="plan-status">{subscription.status === 'active' ? 'Active' : subscription.status}</span>
                </div>
                <div className="plan-price-large">
                  <span className="price">${subscription.price}</span>
                  <span className="period">/month</span>
                </div>
              </div>

            <div className="plan-details">
              {!String(subscription.storage.limitFormatted).toLowerCase().includes('unlimited') && (
                <div className="plan-storage">
                  <div className="plan-storage-header">
                    <FiHardDrive />
                    Storage used
                  </div>
                  <div className="plan-storage-value">
                    <span className="plan-storage-used">{formatBytes(storageUsedBytes)}</span>
                    <span className="plan-storage-of">of {subscription.storage.limitFormatted}</span>
                    <span className="plan-storage-percent">{storageDisplayPercent}%</span>
                  </div>
                  <div className="plan-storage-bar">
                    <div
                      className="plan-storage-fill"
                      style={{
                        width: `${storageBarPercent}%`,
                        backgroundColor: storagePercent > 90 ? '#ef4444' : storagePercent > 75 ? '#f59e0b' : '#22c55e'
                      }}
                    />
                  </div>
                  {showStorageAlert && (
                    <div className={`plan-storage-alert ${storageAlertTone}`}>
                      <FiAlertTriangle />
                      <span>Storage is {storageDisplayPercent}% full. Consider upgrading soon.</span>
                    </div>
                  )}
                </div>
              )}
              {subscription.endDate && (
                <div className="detail-item">
                  <FiCalendar />
                  <span>Next billing: {new Date(subscription.endDate).toLocaleDateString()}</span>
                </div>
              )}
              {subscription.pendingTier && (
                <div className="detail-item">
                  <FiCalendar />
                  <span>Scheduled change: {subscription.pendingTier} on {subscription.pendingEffectiveAt ? new Date(subscription.pendingEffectiveAt).toLocaleDateString() : 'next billing'}</span>
                </div>
              )}
            </div>

            {subscription.cancelAtPeriodEnd && (
              <div className="plan-cancel-notice">
                <FiAlertTriangle />
                <span>
                  Your subscription is set to cancel{subscription.endDate ? ` on ${new Date(subscription.endDate).toLocaleDateString()}` : ' at the end of your current period'}.
                  You'll keep full access until then. Cancellation ends your paid plan; it does not delete your photos or account.
                </span>
              </div>
            )}

            <div className="plan-actions">
              {canonicalTierFromSubscription(subscription) === 'early_access' ? (
                <div className="early-access-notice">
                  <p>You're on the Early Access plan, equivalent to the Personal plan. Explore the plans below to upgrade anytime.</p>
                </div>
              ) : (
                <>
                  {getAvailableUpgrades().length > 0 && (
                    <button onClick={scrollToUpgrade} className="button-primary">
                      <FiArrowUp /> Upgrade Plan
                    </button>
                  )}
                  <button onClick={handleBillingPortal} className="button-secondary">
                    <FiCreditCard /> Manage Billing
                  </button>
                  {subscription.cancelAtPeriodEnd ? (
                    <button onClick={handleResumeSubscription} className="button-secondary" disabled={canceling}>
                      {canceling ? 'Working…' : 'Resume Subscription'}
                    </button>
                  ) : (
                    <button onClick={() => setShowCancelModal(true)} className="button-text-danger" disabled={canceling}>
                      Cancel Subscription
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Feature Comparison */}
      <div className="feature-comparison-section">
        <h3>Feature Comparison</h3>
        <div className="comparison-table">
          <div className="comparison-header">
            <div className="feature-column">Features</div>
            {getTierOrder().map(tierId => (
              <div key={tierId} className="tier-column">
                <strong>{TIER_DETAILS[tierId].name}</strong>
                <span>${TIER_DETAILS[tierId].price}/mo</span>
              </div>
            ))}
          </div>

          {[
            { name: 'Storage', values: { free: '100 GB', pro: '500 GB', studio: '2 TB', business: '5 TB', agency: '12 TB' }, isStorage: true },
            { name: 'Albums', values: { free: '25 max', pro: 'Unlimited', studio: 'Unlimited', business: 'Unlimited', agency: 'Unlimited' } },
            { name: 'Client galleries', values: { free: ', ', pro: ', ', studio: '✓', business: '✓', agency: '✓' } },
            { name: 'Proofing + selection', values: { free: ', ', pro: ', ', studio: '✓', business: '✓', agency: '✓' } },
            { name: 'Client uploads', values: { free: ', ', pro: ', ', studio: ', ', business: '✓', agency: '✓' } },
            { name: 'Team seats', values: { free: '1', pro: '1', studio: '1', business: '5', agency: '20' }, isSeats: true },
            { name: 'Extra seats', values: { free: ', ', pro: '$8/seat', studio: '$8/seat', business: '$8/seat', agency: '$8/seat' } },
            { name: 'The Strip (social)', values: { free: ', ', pro: ', ', studio: '✓', business: '✓', agency: '✓' } },
            { name: 'Preview speed', values: { free: 'Standard', pro: 'Faster', studio: 'Priority', business: 'Priority', agency: 'Priority' } },
            { name: 'Priority compute', values: { free: ', ', pro: ', ', studio: ', ', business: '✓', agency: '✓' } },
            { name: 'Photo Vault', values: { free: ', ', pro: '$24/TB', studio: '$24/TB', business: '$24/TB', agency: '$24/TB' } },
            { name: 'Analytics', values: { free: ', ', pro: 'Basic', studio: 'Basic', business: 'Advanced', agency: 'Advanced' } },
            { name: 'White-label', values: { free: ', ', pro: ', ', studio: ', ', business: ', ', agency: '✓' } },
            { name: 'API access', values: { free: ', ', pro: ', ', studio: ', ', business: ', ', agency: '✓' } },
            { name: 'SLA', values: { free: ', ', pro: ', ', studio: ', ', business: ', ', agency: '✓' } },
            { name: 'Support', values: { free: 'Email (5-7d)', pro: 'Email (3-5d)', studio: 'Email (48-72h)', business: 'Email (24-48h)', agency: 'Priority (<24h)' } },
          ].map((feature, i) => (
            <div key={i} className={`comparison-row ${feature.isStorage ? 'storage-row' : ''} ${feature.isSeats ? 'seats-row' : ''}`}>
              <div className="feature-column">{feature.name}</div>
              {getTierOrder().map(tierId => (
                <div key={tierId} className="tier-column">
                  <span className={feature.values[tierId] === '✓' ? 'included' : feature.values[tierId] === ', ' ? 'excluded' : 'value'}>
                    {feature.values[tierId]}
                  </span>
                </div>
              ))}
            </div>
          ))}

          {/* Upgrade/Downgrade buttons row */}
          <div className="comparison-row action-row" ref={upgradeRef}>
            <div className="feature-column"></div>
              {getTierOrder().map(tierId => {
              const isEarlyAccess = canonicalTierFromSubscription(subscription) === 'early_access';
              const isCurrent = canonicalTierFromSubscription(subscription) === tierId;
              const canUpgrade = getAvailableUpgrades().includes(tierId);
              const canDowngrade = getAvailableDowngrades().includes(tierId);
              const isSelected = selectedTier === tierId;

              return (
                <div key={tierId} className="tier-column">
                  {isEarlyAccess ? (
                    <button className="ea-tier-button" disabled>
                      {tierId === 'free' ? 'Included' : TIER_DETAILS[tierId].name}
                    </button>
                  ) : isCurrent ? (
                    <button className="current-plan-button" disabled>
                      Current Plan
                    </button>
                  ) : canUpgrade ? (
                    <button
                      onClick={() => handleTierAction(tierId)}
                      className="upgrade-tier-button"
                      disabled={upgrading}
                    >
                      {upgrading && isSelected ? 'Processing...' : 'Upgrade'}
                    </button>
                  ) : canDowngrade ? (
                    <button
                      onClick={() => handleTierAction(tierId)}
                      className="downgrade-tier-button"
                      disabled={upgrading}
                    >
                      {upgrading && isSelected ? 'Processing...' : 'Downgrade'}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
          {/* Stripe Integration Status */}
          {stripeLoaded && stripeConfig?.configured && (
            <div className="payment-provider-info">
              <small>
                {stripeConfig.testMode ? '🔧 Test Mode: ' : '🔒 '}
                Secure payments powered by Stripe
              </small>
            </div>
          )}
        </div>

        {/* Pricing Explanation */}
        <div className="pricing-explanation">
          <h4>Pricing Details</h4>
          <div className="pricing-grid">
            <div className="pricing-item storage-addon-item">
              <strong><FiHardDrive /> Extra Storage</strong>
              <p>Need more space? Add additional storage for <strong>$29 per TB per month</strong>.</p>
              {canonicalTierFromSubscription(subscription) !== 'early_access' && (
                <button onClick={openStorageModal} className="add-storage-button">
                  <FiPlus /> Add Storage
                </button>
              )}
              <small className="storage-note-inline">Personal plan: max 2TB add-ons. Agency: contact us for custom storage.</small>
            </div>
            <div className="pricing-item">
              <strong><FiArchive /> Photo Vault</strong>
              <p>Unlimited long-term archive storage for old shoots and client deliveries. Browse anytime with auto-generated previews. <strong>$24 per TB per month</strong>: separate from your plan storage.</p>
              <small className="storage-note-inline">Available on Pro plans and above. Does not count against your plan quota.</small>
            </div>
            <div className="pricing-item">
              <strong>Team Seats</strong>
              <p><strong>Team seats are internal collaborator accounts</strong>: staff who help manage photos and projects. Client access is handled separately via secure links and client portals. Pro plan and above can add extra seats for <strong>$8 per seat per month</strong>.</p>
            </div>
            <div className="pricing-item">
              <strong>Upgrades</strong>
              <p>Upgrade at any time. You'll be charged a prorated amount for the remainder of your billing period, then the new rate going forward.</p>
            </div>
            <div className="pricing-item">
              <strong>Downgrades</strong>
              <p>Downgrade at any time. Changes take effect at your next billing date. Your storage usage must be under the target plan's limit to downgrade.</p>
            </div>
          </div>
          <div className="pricing-summary">
            <p><strong>All plans include:</strong> Private by default photos, secure shareable links with expiration control, RAW file support (CR2, NEF, ARW, DNG, and more), mobile-friendly web app access, and automatic backups.</p>
          </div>
        </div>

        <div className="comparison-mobile">
          {getTierOrder().map(tierId => {
            const tier = TIER_DETAILS[tierId];
            const isEarlyAccess = canonicalTierFromSubscription(subscription) === 'early_access';
            const isCurrent = canonicalTierFromSubscription(subscription) === tierId;
            const canUpgrade = getAvailableUpgrades().includes(tierId);
            const canDowngrade = getAvailableDowngrades().includes(tierId);
            const isSelected = selectedTier === tierId;

            return (
              <div key={tierId} className="tier-mobile-card">
                <div className="tier-mobile-header">
                  <div>
                    <h4>{tier.name}</h4>
                    <p>${tier.price}/mo</p>
                  </div>
                  <span className={`tier-mobile-badge ${isCurrent ? 'is-current' : ''}`}>
                    {isEarlyAccess ? 'Early Access' : isCurrent ? 'Current Plan' : 'Available'}
                  </span>
                </div>
                <div className="tier-mobile-meta">
                  <span>Storage: {tier.storage}</span>
                  <span>{tier.albums}</span>
                </div>
                <ul>
                  {tier.features.map((feature) => (
                    <li key={feature}>
                      <FiCheck />
                      {feature}
                    </li>
                  ))}
                  {tier.limitations.map((limit) => (
                    <li key={limit} className="is-limited">
                      <FiX />
                      {limit}
                    </li>
                  ))}
                </ul>
                <div className="tier-mobile-actions">
                  {isEarlyAccess ? (
                    <button className="ea-tier-button" disabled>
                      {tierId === 'free' ? 'Included' : tier.name}
                    </button>
                  ) : isCurrent ? (
                    <button className="current-plan-button" disabled>
                      Current Plan
                    </button>
                  ) : canUpgrade ? (
                    <button
                      onClick={() => handleTierAction(tierId)}
                      className="upgrade-tier-button"
                      disabled={upgrading}
                    >
                      {upgrading && isSelected ? 'Processing...' : 'Upgrade'}
                    </button>
                  ) : canDowngrade ? (
                    <button
                      onClick={() => handleTierAction(tierId)}
                      className="downgrade-tier-button"
                      disabled={upgrading}
                    >
                      {upgrading && isSelected ? 'Processing...' : 'Downgrade'}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Billing Error Alert */}
      {billingError && (
        <div className="billing-error-alert">
          <FiAlertTriangle />
          <span>{billingError}</span>
          <button onClick={() => setBillingError(null)}><FiX /></button>
        </div>
      )}

      {/* Manage Billing Modal */}
      {showBillingModal && (
        <div className="billing-modal-overlay" onClick={() => setShowBillingModal(false)}>
          <div className="billing-modal" onClick={e => e.stopPropagation()}>
            <div className="billing-modal-header">
              <h3>Manage Billing</h3>
              <button onClick={() => setShowBillingModal(false)} className="modal-close-btn">
                <FiX />
              </button>
            </div>
            <div className="billing-modal-body">
              {stripeConfig?.configured ? (
                <>
                  <div className="billing-info-card">
                    <FiCreditCard className="billing-icon" />
                    <h4>No payment method on file</h4>
                    <p>To manage your billing, you need to set up a payment method by upgrading to a paid plan.</p>
                  </div>
                  <div className="billing-actions">
                    <button className="button-primary" onClick={() => { setShowBillingModal(false); scrollToUpgrade(); }}>
                      <FiArrowUp /> View Plans
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="billing-info-card test-mode">
                    <FiAlertTriangle className="billing-icon" />
                    <h4>Test Mode Active</h4>
                    <p>Stripe billing is not configured for this environment. Contact support for billing inquiries.</p>
                  </div>
                  <div className="billing-contact">
                    <p><FiMail /> <a href="mailto:support@dailey.photos">support@dailey.photos</a></p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Change Plan Confirmation Modal */}
      {showChangePlanModal && targetTier && (
        <div className="billing-modal-overlay" onClick={() => setShowChangePlanModal(false)}>
          <div className="billing-modal plan-change-modal" onClick={e => e.stopPropagation()}>
            <div className="billing-modal-header">
              <h3>{isDowngrade(targetTier) ? 'Downgrade Plan' : 'Upgrade Plan'}</h3>
              <button onClick={() => setShowChangePlanModal(false)} className="modal-close-btn">
                <FiX />
              </button>
            </div>
            <div className="billing-modal-body">
              <div className="plan-change-comparison">
                <div className="plan-from">
                  <span className="plan-label">Current Plan</span>
                  <span className="plan-name">{subscription?.tierName}</span>
                  <span className="plan-price">${subscription?.price}/mo</span>
                </div>
                <div className="plan-arrow">
                  {isDowngrade(targetTier) ? <FiArrowDown /> : <FiArrowUp />}
                </div>
                <div className="plan-to">
                  <span className="plan-label">New Plan</span>
                  <span className="plan-name">{TIER_DETAILS[targetTier]?.name}</span>
                  <span className="plan-price">${TIER_DETAILS[targetTier]?.price}/mo</span>
                </div>
              </div>

              {isDowngrade(targetTier) ? (
                <div className="plan-change-info downgrade">
                  <FiCalendar />
                  <p>Your plan will change at the end of your current billing period. You'll keep access to your current features until then.</p>
                </div>
              ) : (
                <div className="plan-change-info upgrade">
                  <FiCheck />
                  <p>Your plan will upgrade immediately. You'll be charged a prorated amount for the remainder of your billing period.</p>
                </div>
              )}

              <div className="plan-change-features">
                <h4>What you'll {isDowngrade(targetTier) ? 'lose' : 'get'}:</h4>
                <ul>
                  <li>Storage: {TIER_DETAILS[targetTier]?.storage}</li>
                  {TIER_DETAILS[targetTier]?.features.slice(0, 3).map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="billing-modal-footer">
              <button className="button-secondary" onClick={() => setShowChangePlanModal(false)}>
                Cancel
              </button>
              <button
                className={isDowngrade(targetTier) ? 'button-warning' : 'button-primary'}
                onClick={confirmPlanChange}
                disabled={upgrading}
              >
                {upgrading ? 'Processing...' : isDowngrade(targetTier) ? 'Confirm Downgrade' : 'Upgrade Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="billing-modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="billing-modal plan-change-modal" onClick={e => e.stopPropagation()}>
            <div className="billing-modal-header">
              <h3>Cancel Subscription</h3>
              <button onClick={() => setShowCancelModal(false)} className="modal-close-btn">
                <FiX />
              </button>
            </div>
            <div className="billing-modal-body">
              <div className="plan-change-info downgrade">
                <FiAlertTriangle />
                <p>
                  Cancelling stops renewal of your Photo Harbor subscription. You'll keep plan access until
                  {subscription?.endDate ? ` ${new Date(subscription.endDate).toLocaleDateString()}` : ' the end of your current period'},
                  and your card won't be charged again. If you're still in your free trial, you won't be charged at all.
                </p>
              </div>
              <div className="plan-change-info downgrade">
                <FiAlertTriangle />
                <p>
                  Cancellation does not delete your photos or albums. Paid features may become unavailable
                  after your plan ends. You can resume before the cancellation date.
                </p>
              </div>
            </div>
            <div className="billing-modal-footer">
              <button className="button-secondary" onClick={() => setShowCancelModal(false)} disabled={canceling}>
                Keep Subscription
              </button>
              <button className="button-warning" onClick={handleCancelSubscription} disabled={canceling}>
                {canceling ? 'Cancelling…' : 'Cancel Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Storage Modal */}
      {showStorageModal && (
        <div className="billing-modal-overlay" onClick={() => setShowStorageModal(false)}>
          <div className="billing-modal storage-modal" onClick={e => e.stopPropagation()}>
            <div className="billing-modal-header">
              <h3><FiHardDrive /> Add Extra Storage</h3>
              <button onClick={() => setShowStorageModal(false)} className="modal-close-btn">
                <FiX />
              </button>
            </div>
            <div className="billing-modal-body">
              {storageInfo && (
                <div className="storage-current-info">
                  <div className="storage-stat">
                    <span className="storage-label">Current Usage</span>
                    <span className="storage-value">{storageInfo.usedFormatted}</span>
                  </div>
                  <div className="storage-stat">
                    <span className="storage-label">Plan Storage</span>
                    <span className="storage-value">{storageInfo.baseLimitFormatted}</span>
                  </div>
                  {storageInfo.extraStorage > 0 && (
                    <div className="storage-stat">
                      <span className="storage-label">Extra Storage</span>
                      <span className="storage-value">+ {storageInfo.extraStorageFormatted}</span>
                    </div>
                  )}
                  <div className="storage-stat total">
                    <span className="storage-label">Total Available</span>
                    <span className="storage-value">{storageInfo.totalLimitFormatted}</span>
                  </div>
                </div>
              )}

              <div className="storage-selector">
                <label>How much storage do you need?</label>
                <div className="storage-amount-control">
                  <button
                    onClick={() => setStorageAmount(Math.max(1, storageAmount - 1))}
                    disabled={storageAmount <= 1}
                    className="amount-btn"
                  >
                    <FiMinus />
                  </button>
                  <div className="storage-amount-display">
                    <span className="amount-value">{storageAmount}</span>
                    <span className="amount-unit">TB</span>
                  </div>
                  <button
                    onClick={() => setStorageAmount(Math.min(10, storageAmount + 1))}
                    disabled={storageAmount >= 10}
                    className="amount-btn"
                  >
                    <FiPlus />
                  </button>
                </div>
                <div className="storage-price-display">
                  <span className="price-label">Monthly cost:</span>
                  <span className="price-value">${storageAmount * 29}/month</span>
                </div>
              </div>

              <div className="storage-note">
                <p>Storage add-ons are billed monthly and can be removed at any time through the billing portal.</p>
              </div>
            </div>
            <div className="billing-modal-footer">
              <button className="button-secondary" onClick={() => setShowStorageModal(false)}>
                Cancel
              </button>
              <button
                className="button-primary"
                onClick={handleAddStorage}
                disabled={addingStorage}
              >
                {addingStorage ? 'Processing...' : `Add ${storageAmount} TB for $${storageAmount * 29}/mo`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
